import { getBaseThemeName } from "@/features/branding";
import { useMaxAssetCountForCollectionHome } from "@/features/collection_home/utils";
import { InteractiveTutorial, InteractiveTutorialProps } from "@/features/interactive_tutorial";
import { maxLssPoints } from "@/tools/create/lss-from-map/constants";
import { useLssTutorialState } from "@/tools/risk-bands/large-site-screen/context/LssTutorialContext";
import { LIST_STEPS, MAP_STEP_COUNT, TOTAL_TOUR_STEPS } from "@/utils/lssTourStepConstants";
import { MoreOutlined } from "@ant-design/icons";
import { Alert, Image, TourStepProps } from "antd";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** Whether the current sessionStorage handoff payload is tutorial-driven demo data. */
const isResumingTutorialHandoff = () => {
  if (typeof window === "undefined") return false;
  try {
    const data = sessionStorage.getItem("largeSiteScreenHandoffData");
    if (!data) return false;
    return !!JSON.parse(data).isTutorial;
  } catch {
    return false;
  }
};

/** Whether the sessionStorage flag for resuming at START_RUN (set when the user clicked
 * "Previous" from the first step of the map-page tour) is present. */
const isResumingAtStartRun = () => {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem("largeSiteScreenResumeAtStartRun") === "1";
};
interface LargeSiteScreenTutorialProps extends InteractiveTutorialProps<Record<any, any>> {
  setIsTutorialOpen: (open: boolean) => void;
}
export const LargeSiteScreenTutorial = ({
  setIsTutorialOpen,
  ...props
}: LargeSiteScreenTutorialProps) => {
  const router = useRouter();
  const {
    startNewRunBtn,
    analysisLibrary,
    libraryFilters,
    reviewSection,
    usageSection,
    runAnalysisBtn,
    advancedSettingsRefs,
    setShowTutorialProcessingRow,
    setShowTutorialCompletedRow,
    closeReviewModal,
    openReviewModal,
    portfolioBridge
  } = useLssTutorialState();
  const maxCollectionPoints = useMaxAssetCountForCollectionHome();
  const {
    t: settings
  } = useTranslation("tools", {
    keyPrefix: "settings"
  });
  const [current, setCurrent] = useState<number>(() => {
    if (isResumingTutorialHandoff()) return LIST_STEPS.REVIEW;
    if (isResumingAtStartRun()) return LIST_STEPS.START_RUN;
    return LIST_STEPS.NAV;
  });
  useEffect(() => {
    // Resuming from the map page's handoff - reopen the tour where it left off.
    if (isResumingTutorialHandoff() && Object.keys(router.query).includes("handoff")) {
      setCurrent(LIST_STEPS.REVIEW);
      setIsTutorialOpen(true);
    } else if (isResumingAtStartRun() && Object.keys(router.query).includes("tutorial-resume")) {
      // Resuming after clicking "Previous" from the map page's first step.
      setCurrent(LIST_STEPS.START_RUN);
      setIsTutorialOpen(true);
    }
    // Consume the resume signals so a later remount (navigating away and back, or a page
    // refresh) doesn't keep resuming into these steps indefinitely - they should only apply once,
    // right after the handoff/back-navigation that set them.
    if (isResumingTutorialHandoff()) {
      sessionStorage.removeItem("largeSiteScreenHandoffData");
    }
    if (isResumingAtStartRun()) {
      sessionStorage.removeItem("largeSiteScreenResumeAtStartRun");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Several of the later steps' targets (editModal, deleteModal, outputTypeModal, summaryQueueModal,
  // portfolioItem.*) only mount/populate after PortfolioTutorialContext's debounced setters fire
  // (~300ms), and since ref mutation doesn't trigger a re-render, Tour's target() would otherwise
  // never be re-evaluated once that ref becomes available. Force one extra re-render shortly after
  // each step change so Tour picks up the now-current target.
  const [, forceRetarget] = useState(0);
  useEffect(() => {
    const timeout = setTimeout(() => forceRetarget(n => n + 1), 600);
    return () => clearTimeout(timeout);
  }, [current]);
  useEffect(() => {
    setShowTutorialProcessingRow(!!props.open && current === LIST_STEPS.PROCESSING);
    setShowTutorialCompletedRow(!!props.open && current === LIST_STEPS.COMPLETED);
  }, [props.open, current, setShowTutorialProcessingRow, setShowTutorialCompletedRow]);
  const steps: TourStepProps[] = [{
    title: "Launch the Tool",
    description: <p>
          On the main navigation menu, under <strong>&quot;Comprehensive Tools,&quot;</strong> click on{" "}
          <strong>&quot;Large Site Analysis.&quot;</strong>
        </p>,
    target: () => null
  }, {
    title: "Analysis Library",
    description: <p>
          You can access previous analyses from the Analysis Library. Only those shared with you will be visible.
          Private analyses are only visible to those who created them.
        </p>,
    target: () => analysisLibrary.current
  }, {
    title: "Filters",
    description: <p>Use the filters above the Analysis Library to filter the list by user or group.</p>,
    target: () => libraryFilters.current
  }, {
    title: "Start a New Analysis",
    description: <p>
          To start a new analysis, click <strong>&quot;Start New Run.&quot;</strong>
        </p>,
    target: () => startNewRunBtn.current,
    placement: "left"
  }, {
    title: "Review & Configure Analysis",
    description: <p>
          A review modal will appear confirming the details of your Large Site Analysis. Here you can name the analysis,
          select sharing permissions, and see the total polygon area and number of grid points included. If you select{" "}
          <strong>&quot;Private&quot;</strong> your large site will be only visible to you, while{" "}
          <strong>&quot;Shared&quot;</strong> analyses will be visible to other users in the selected group.
        </p>,
    target: () => reviewSection.current
  }, {
    title: settings("advancedAnalysisSettings"),
    description: <p>
          Under <strong>&quot;{settings("advancedAnalysisSettings")}&quot;</strong> choose whether to enable or disable{" "}
          {settings("anomalyManagementRp.title")} and {settings("defences.title")}.
        </p>,
    target: () => (advancedSettingsRefs?.advancedTogglesSection.current ?? null) as HTMLElement
  }, {
    title: "Riskiest Point Settings",
    description: <p>
          Under <strong>&quot;Riskiest Point Settings,&quot;</strong> select the year and hazard(s) you want to use to
          define the riskiest grid point criteria. The riskiest point refers to the single grid point in your large site
          polygon with the highest Maximum-to-date Value-At-Risk (MVAR), based on your selected criteria.
        </p>,
    target: () => (advancedSettingsRefs?.riskiestPointSection.current ?? null) as HTMLElement
  }, {
    title: "Usage After Upload",
    description: <p>Finally, review your current usage against the allocation available to your organisation.</p>,
    target: () => usageSection.current
  }, {
    title: "Run Analysis",
    description: <p>
          Click <strong>&quot;Run Analysis&quot;</strong> to run the Large Site Analysis.
        </p>,
    target: () => runAnalysisBtn.current,
    placement: "top"
  }, {
    title: "Processing Your Analysis",
    description: <>
          <p className="mb-2">
            The Large Site Analysis will begin to run. Once the points have uploaded, the status will change to blue and{" "}
            <strong>&quot;Processing.&quot;</strong>
          </p>
          <Alert type="warning" showIcon message="Do not close your browser while the analysis is running." />
        </>,
    target: () => (portfolioBridge?.analysisRun.item.current ?? null) as unknown as HTMLElement
  }, {
    title: "Analysis Complete",
    description: <p>Once processing is complete, the status will change to a green tick.</p>,
    target: () => (portfolioBridge?.analysisRun.item.current ?? null) as unknown as HTMLElement
  }, {
    title: "Generating Summary",
    description: <>
          <p className="mb-2">
            Your site-level aggregated summary will automatically generate once your analysis is finished processing. In
            the top-right corner of the page, an out tray will appear showing your aggregated summary&apos;s position in
            the processing queue.
          </p>
          <div className="mb-2 flex justify-center">
            <Image alt="Button that shows aggregated summary generation position in queue" src={`/img/tours/portfolio-insights/summary_modal_queue_trigger${getBaseThemeName() === "hkma" ? "_hkma" : ""}.png`} />
          </div>
          <p className="mb-2">Once it reaches the front of the queue, processing will begin automatically.</p>
          <Alert type="info" showIcon message="The time to generate will depend on the number of summaries (both within and outside your institution) in queue and the size of those analyses." />
        </>,
    target: () => (portfolioBridge?.summaryQueueModal.current ?? null) as unknown as HTMLElement
  }, {
    title: "Summary Generated",
    description: <p>You will be notified that your aggregated summary is ready for download when the processing is complete.</p>,
    target: () => null,
    placement: "top"
  }, {
    title: "View Results",
    description: <>
          <p className="mb-2">
            To view results on a dashboard click <strong>&quot;View Results.&quot;</strong> Results may take a few
            moments to load, depending on the size of the analysis.
          </p>
          {maxLssPoints > maxCollectionPoints && <Alert type="info" showIcon message={`Only analyses with ${maxCollectionPoints} points or fewer can be visualised on the results dashboard. For larger analyses, this option will be disabled.`} />}
        </>,
    target: () => (portfolioBridge?.portfolioItem.viewResultsTrigger.current ?? null) as unknown as HTMLElement,
    placement: "left"
  }, {
    title: "Download Aggregated Results",
    description: <p>
          To view an aggregated summary file that contains climate risk results aggregated across the entire large site,
          click <strong>&quot;Download&quot;</strong> then <strong>&quot;Aggregated Summary Results&quot;</strong>{" "}
          button.
        </p>,
    target: (portfolioBridge?.portfolioItem.downloadsMenu.current ?? null) as unknown as HTMLElement,
    placement: "left"
  }, {
    title: "Download Point-Level Results",
    description: <p>
          Click <strong>&quot;Point-Level Results&quot;</strong> to download a .csv file of climate risk results for
          each grid point within the large site.
        </p>,
    target: () => (portfolioBridge?.portfolioItem.downloadsMenu.current ?? null) as unknown as HTMLElement,
    placement: "left"
  }, {
    title: "Select Point-Level Download",
    description: <p>
          Select the type of result output and file type desired from the list of options provided. Once downloading is
          completed, the file will be saved to your downloads folder.
        </p>,
    target: () => (portfolioBridge?.outputTypeModal.current ?? null) as unknown as HTMLElement
  }, {
    title: "Download Metadata Package",
    description: <p>
          Click <strong>&quot;Metadata Package&quot;</strong> to download a zip file of relevant input settings and
          underlying data layers used to produce the climate risk results.
        </p>,
    target: (portfolioBridge?.portfolioItem.downloadsMenu.current ?? null) as unknown as HTMLElement,
    placement: "left"
  }, {
    title: "Edit or Delete",
    description: <p>
          To update the name or sharing permissions of an existing analysis, click the vertical three-dot menu{" "}
          <MoreOutlined /> and select <strong>&quot;Edit.&quot;</strong>
        </p>,
    target: () => (portfolioBridge?.portfolioItem.actionsMenu.current ?? null) as unknown as HTMLElement,
    placement: "top"
  }, {
    title: "Edit Saved Analysis",
    description: <p>
          Update the name or sharing permissions of an existing analysis, and then click{" "}
          <strong>&quot;Save.&quot;</strong>
        </p>,
    target: () => (portfolioBridge?.editModal.current ?? null) as unknown as HTMLElement,
    placement: "bottom"
  }, {
    title: "Delete Saved Analysis",
    description: <p>Deleting a large site analysis will permanently remove it for all members of your organisational group.</p>,
    target: () => (portfolioBridge?.deleteModal.current ?? null) as unknown as HTMLElement
  }];
  return <InteractiveTutorial<Record<any, any>> isWide steps={steps} {...props} current={current} indicatorsRender={localCurrent => {
    const stageOffset = localCurrent < LIST_STEPS.REVIEW ? 0 : MAP_STEP_COUNT;
    return <div className="flex items-center gap-2">
            <span>
              {localCurrent + 1 + stageOffset} / {TOTAL_TOUR_STEPS}
            </span>
          </div>;
  }} onChange={step => {
    // Leaving the last leg-1 step navigates away for the map-drawing leg of the tour —
    // let the page unmount instead of advancing this Tour instance locally.
    if (current === LIST_STEPS.START_RUN && step === LIST_STEPS.REVIEW) {
      router.push("/tools/create/lss-from-map?tutorial");
      return;
    }
    // Clicking "Previous" from the first Stage-3 step (REVIEW) should cross back to the map
    // page's last step, not land on START_RUN within this same Tour instance.
    if (current === LIST_STEPS.REVIEW && step === LIST_STEPS.START_RUN) {
      sessionStorage.setItem("lssFromMapResumeAtLastStep", "1");
      router.push("/tools/create/lss-from-map?tutorial");
      return;
    }
    if (step === LIST_STEPS.PROCESSING) {
      closeReviewModal();
    }
    // Going backward from Processing to Run Analysis needs the review modal reopened - closing
    // it (above) unmounts the Run Analysis button, so without this the highlight has nothing
    // to target.
    if (current === LIST_STEPS.PROCESSING && step === LIST_STEPS.RUN_ANALYSIS) {
      openReviewModal();
    }
    portfolioBridge?.setShowSummaryModal(step === LIST_STEPS.GENERATING_SUMMARY);
    if (step === LIST_STEPS.SUMMARY_GENERATED) {
      portfolioBridge?.handleSummaryStep();
    } else {
      portfolioBridge?.handleSummaryGenerated();
    }
    portfolioBridge?.setShowDownloadDropdown(step === LIST_STEPS.DOWNLOAD_AGGREGATED || step === LIST_STEPS.DOWNLOAD_POINT_LEVEL || step === LIST_STEPS.DOWNLOAD_METADATA);
    if (step === LIST_STEPS.DOWNLOAD_AGGREGATED) {
      portfolioBridge?.setSelectedDownloadItemIndex(0);
    } else if (step === LIST_STEPS.DOWNLOAD_POINT_LEVEL) {
      portfolioBridge?.setSelectedDownloadItemIndex(1);
    } else if (step === LIST_STEPS.DOWNLOAD_METADATA) {
      portfolioBridge?.setSelectedDownloadItemIndex(2);
    } else {
      portfolioBridge?.setSelectedDownloadItemIndex(undefined);
    }
    portfolioBridge?.setShowPointLevelResultsModal(step === LIST_STEPS.POINT_LEVEL_MODAL);
    portfolioBridge?.setShowMoreDropdown(step === LIST_STEPS.ACTIONS_MENU);
    portfolioBridge?.setShowTutorialEditModal(step === LIST_STEPS.EDIT_MODAL);
    portfolioBridge?.setShowTutorialDeleteModal(step === LIST_STEPS.DELETE_MODAL);
    setCurrent(step);
  }} onClose={() => {
    closeReviewModal();
    portfolioBridge?.setShowSummaryModal(false);
    portfolioBridge?.handleSummaryGenerated();
    portfolioBridge?.setShowDownloadDropdown(false);
    portfolioBridge?.setSelectedDownloadItemIndex(undefined);
    portfolioBridge?.setShowPointLevelResultsModal(false);
    portfolioBridge?.setShowMoreDropdown(false);
    portfolioBridge?.setShowTutorialEditModal(false);
    portfolioBridge?.setShowTutorialDeleteModal(false);
    setCurrent(LIST_STEPS.NAV);
    setIsTutorialOpen(false);
  }} onFinish={() => {
    setCurrent(LIST_STEPS.NAV);
    setShowTutorialProcessingRow(false);
    setShowTutorialCompletedRow(false);
    portfolioBridge?.setShowSummaryModal(false);
    portfolioBridge?.handleSummaryGenerated();
    portfolioBridge?.setShowDownloadDropdown(false);
    portfolioBridge?.setSelectedDownloadItemIndex(undefined);
    portfolioBridge?.setShowPointLevelResultsModal(false);
    portfolioBridge?.setShowMoreDropdown(false);
    portfolioBridge?.setShowTutorialEditModal(false);
    portfolioBridge?.setShowTutorialDeleteModal(false);
    setIsTutorialOpen(false);
  }} data-sentry-element="InteractiveTutorial" data-sentry-component="LargeSiteScreenTutorial" data-sentry-source-file="LargeSiteScreenTutorial.tsx" />;
};